import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { scannerApi, apiMessage, extractList } from 'shared/api/scanner'
import { useAuth } from 'context/AuthContext'
import { PERMISSIONS, hasPermission } from 'shared/util/permissions'
import { PermissionEmpty, EmptyState, Badge, Skeleton } from 'shared/components/ui/primitives'
import SearchBar from 'shared/components/searchbar'
import { convertDateFormat2 } from 'shared/util'

function Coupons() {
  const { eventId } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [items, setItems] = useState([])
  const [searchItem, setSearchItem] = useState('')
  const [debounced, setDebounced] = useState('')
  const [skip, setSkip] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(true)
  const canAdd = hasPermission(user, eventId, PERMISSIONS.ADD_COUPON)
  const canEdit = hasPermission(user, eventId, PERMISSIONS.EDIT_COUPON)
  const canDelete = hasPermission(user, eventId, PERMISSIONS.DELETE_COUPON)

  const load = useCallback(
    async (reset = false, nextSkip = 0) => {
      try {
        if (reset) setLoading(true)
        const res = await scannerApi.coupons(eventId, { sSearch: debounced, nSkip: nextSkip, nLimit: 10 })
        const next = extractList(res, ['aCoupon', 'aCoupons', 'aEvent', 'aData'])
        setItems((prev) => (reset ? next : [...prev, ...next]))
        setHasMore(next.length >= 10)
      } catch (err) {
        toast.error(apiMessage(err))
        if (reset) setItems([])
      } finally {
        setLoading(false)
      }
    },
    [debounced, eventId]
  )

  useEffect(() => {
    const id = setTimeout(() => setDebounced(searchItem), 400)
    return () => clearTimeout(id)
  }, [searchItem])

  useEffect(() => {
    setSkip(0)
    load(true, 0)
  }, [load])

  const remove = async (coupon) => {
    if (!window.confirm(`Delete coupon ${coupon.sCode}?`)) return
    try {
      await scannerApi.deleteCoupon(coupon._id, eventId)
      setItems((prev) => prev.filter((item) => item._id !== coupon._id))
      toast.success('Coupon deleted')
    } catch (err) {
      toast.error(apiMessage(err))
    }
  }

  if (!hasPermission(user, eventId, [PERMISSIONS.LIST_COUPON, PERMISSIONS.COUPON_REPORT])) return <PermissionEmpty />

  return (
    <div className="relative mx-auto w-full max-w-3xl px-4 py-5">
      <h2 className="mb-3 text-4xl font-bold">Coupons</h2>
      <SearchBar searchItem={searchItem} onChangeFn={setSearchItem} placeHolder="Search coupons" className="mb-4" />

      {loading && items.length === 0
        ? (
        <Skeleton className="h-28" />
          )
        : items.length === 0
          ? (
        <EmptyState title="No coupons" message="Create a coupon for this event." />
            )
          : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item._id} className="overflow-hidden rounded-card bg-surface">
              <div className="flex">
                <div className="flex w-28 flex-col items-center justify-center bg-theme px-2 py-4 text-center text-light">
                  <p className="mb-1 text-sm font-bold">{item.sCode}</p>
                  <p className="mb-0 text-xs">
                    {item.eDiscountType === 'P' ? `${item.nDiscount}% off` : `₹${item.nDiscount} off`}
                  </p>
                </div>
                <div className="flex-1 p-3">
                  <div className="mb-1 flex items-center justify-between">
                    <p className="mb-0 text-sm font-semibold">
                      Used {item.nRedeemCount || 0}/{item.nCount || 0}
                    </p>
                    <Badge tone={item.eStatus === 'a' ? 'success' : 'neutral'}>{item.eStatus === 'a' ? 'Active' : 'Inactive'}</Badge>
                  </div>
                  <p className="mb-1 text-xs text-muted">
                    {item.eDiscountType === 'P' && item.nMaxDiscountAmount ? `Cap ₹${item.nMaxDiscountAmount}` : ''}
                    {item.eDiscountType === 'F' && item.nMinAmountToApplyCode ? `Min ₹${item.nMinAmountToApplyCode}` : ''}
                  </p>
                  <p className="mb-2 text-xs text-muted">Expires {item.dExpireDate ? convertDateFormat2(item.dExpireDate) : '—'}</p>
                  <div className="flex gap-3 text-sm font-semibold">
                    {canEdit && (
                      <button type="button" className="text-theme" onClick={() => navigate(`/events/${eventId}/coupons/${item._id}`, { state: { coupon: item } })}>
                        Edit
                      </button>
                    )}
                    {canDelete && (
                      <button type="button" className="text-danger" onClick={() => remove(item)}>
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
            {hasMore && !loading && (
              <button
                type="button"
                className="min-h-tap w-full text-sm font-semibold text-theme"
                onClick={() => {
                  const next = skip + 10
                  setSkip(next)
                  load(false, next)
                }}
              >
                Load more
              </button>
            )}
        </div>
            )}

      {canAdd && (
        <button
          type="button"
          onClick={() => navigate(`/events/${eventId}/coupons/new`)}
          className="fixed bottom-24 right-4 z-30 flex h-14 min-w-14 items-center justify-center rounded-full bg-theme px-5 font-bold text-light shadow-glow lg:bottom-8"
        >
          + Coupon
        </button>
      )}
    </div>
  )
}

export default Coupons
